import React, {
  DragEventHandler,
  MouseEvent,
  MouseEventHandler,
  useEffect,
  useState
} from 'react';
import styles from './index.module.css';
import { AssetVersion, DesignAsset } from './types';
import { formatDistanceStrict, parseISO } from 'date-fns';
import AssetMenu from './AssetMenu';
import TaskCount from './TaskCount';
import AssetName from './AssetName';
import AssetImage, { HoverShareButton } from './AssetImage';
import { Document, Page } from 'appJS/utils/pdfjs_worker';
import cx from 'classnames';
import Loader from '../../../clients/sidebar/components/Loader';
// @ts-expect-error
import FigmaIcon from './assets/FigmaLogo';
import ShareButton from 'appJS/components/ShareButton';
import { UNGROUPED } from './AssetsScreen';
import * as translations from '../design_assets/strings';
import { getLangKey } from 'appJS/models/Language';
import { handleCopy } from 'appJS/utils/handleCopy';
import VersionSelector from './VersionSelector';
import { useAssetVersioningState } from './AssetVersioningContext';

const strings = translations[getLangKey()];

type Props = {
  asset: DesignAsset;
  container: HTMLDivElement;
  canManageAssets?: boolean;
  disabled?: boolean;
  setData?: (data: DesignAsset[]) => void;
  bugherdUrl: string;
  existingGroups?: string[];
  handleSharingClick?: () => void;
  onAddToGroup?: (groupName: string) => void;
  openNewGroupModal?: () => void;
  isGuest?: boolean;
  index?: number;
  useShareLink?: boolean;
  shareToken?: string;
  loggedIn?: boolean;
  preferredVersion?: number;
};

export const AssetCard: React.FC<Props> = ({
  asset,
  container,
  canManageAssets,
  disabled,
  existingGroups,
  setData,
  handleSharingClick,
  onAddToGroup,
  openNewGroupModal,
  isGuest,
  index,

  useShareLink,
  shareToken,
  loggedIn,
  preferredVersion
}) => {
  const {
    id,
    name,
    fileType,
    projectId,
    openTasksCount,
    triageTasksCount,
    createdAt,
    updatedAt,
    isFigma
  } = asset;
  const assetVersioning = useAssetVersioningState();
  const hasAssetVersioningExperiment = assetVersioning?.enabled || false;
  const versions = hasAssetVersioningExperiment ? asset.versions || [] : [];
  const latestVersion = versions[versions.length - 1];
  const fallbackVersion: AssetVersion = {
    version: 1,
    createdAt,
    url: asset.url,
    fileName: name,
    fileType
  };
  const [selectedVersion, setSelectedVersion] = useState<
    AssetVersion | undefined
  >(latestVersion);
  useEffect(() => {
    if (!hasAssetVersioningExperiment) return;
    const match = preferredVersion
      ? (asset.versions || []).find(
          version => version.version === preferredVersion
        )
      : undefined;
    setSelectedVersion(match || asset.versions?.[asset.versions.length - 1]);
  }, [asset.id, asset.versions, hasAssetVersioningExperiment, preferredVersion]);
  const displayedVersion = selectedVersion || latestVersion || fallbackVersion;
  const selectedUrl = hasAssetVersioningExperiment
    ? displayedVersion?.url || asset.url
    : asset.url;
  const selectedFileType = hasAssetVersioningExperiment
    ? displayedVersion?.fileType || fileType
    : fileType;
  const isPdf = selectedFileType === 'pdf';
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const assetEndpoint = `/projects/${projectId}/assets/${id}`;
  const assetHref =
    useShareLink && shareToken
      ? `/share/${shareToken}`
      : `${assetEndpoint}${
          hasAssetVersioningExperiment && displayedVersion?.version
            ? `?asset_version=${displayedVersion.version}`
            : ''
        }`;
  const checkFileType = (isPdf && 'PDF') || (isFigma && 'FIGMA') || 'IMAGE';
  const showMenu = !!setData;
  const canManageVersions =
    hasAssetVersioningExperiment && !!canManageAssets && !disabled && !isGuest;
  const canSyncFigma = canManageVersions && !!isFigma;
  const handleDragStart: DragEventHandler<HTMLAnchorElement> = event => {
    if (isEditing) event.preventDefault();
  };

  const onShareButtonClick: MouseEventHandler<HTMLElement> = (
    event: MouseEvent<HTMLElement, globalThis.MouseEvent>
  ) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!showMenu) {
      const assetURL = new URL(window.location.href);
      assetURL.pathname = assetEndpoint;
      handleCopy({
        text: assetURL.href,
        successMessage: `URL for ${name} copied to clipboard ✅`
      });
    } else if (handleSharingClick) handleSharingClick();
  };

  const getCardAction = () => {
    const showMenu = !!setData;
    if (showMenu && !isGuest) {
      return (
        <AssetMenu
          container={container}
          assetEndpoint={assetEndpoint}
          onRenameClick={() => setIsEditing(true)}
          canManageAssets={!!canManageAssets}
          disabled={!!disabled}
          setData={setData}
          groupNames={
            existingGroups?.filter(group => group !== UNGROUPED) || []
          }
          asset={asset}
          handleSharingClick={() => handleSharingClick?.()}
          onAddToGroup={onAddToGroup}
          openNewGroupModal={openNewGroupModal}
        />
      );
    }
    return <ShareButton onClick={onShareButtonClick} iconOnly />;
  };

  return (
    <a
      href={assetHref}
      className={cx(styles.card, styles.staggerInAnimation, {
        [styles.guestCard]: !canManageAssets
      })}
      target="_self"
      style={{ '--index': index }}
      draggable={isEditing ? false : undefined}
      onDragStart={handleDragStart}
    >
      <TaskCount
        container={container}
        openTasksCount={openTasksCount}
        triageTasksCount={triageTasksCount}
      />

      {isPdf ? (
        <>
          <Document
            file={selectedUrl}
            loading={<Loader useDarkStyles className={styles.loaderIcon} />}
            className={cx(styles.pdfDocument, { [styles.grayscale]: disabled })}
          >
            <Page
              className={styles.pdfPage}
              pageNumber={1}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={<Loader useDarkStyles className={styles.loaderIcon} />}
            />
            <HoverShareButton
              onShareClick={onShareButtonClick}
              id={id}
              projectId={projectId}
              loggedIn={loggedIn}
            />
          </Document>
          {disabled && (
            <div className={styles.archived}>{strings.archived}</div>
          )}
        </>
      ) : (
        <AssetImage
          url={selectedUrl}
          id={id}
          projectId={projectId}
          showShareHover
          disabled={!!disabled}
          onShareClick={onShareButtonClick}
          loggedIn={loggedIn}
        />
      )}
      <div className={styles.cardContent}>
        <AssetName
          assetEndpoint={assetEndpoint}
          name={name}
          fileType={fileType}
          setIsEditing={setIsEditing}
          isEditing={isEditing}
          canManageAssets={!!canManageAssets && !isGuest}
        />
        <div className={styles.assetTimestamps}>
          <p className={styles.lastUpdated}>
            Created at: {formatDistanceStrict(new Date(), parseISO(createdAt))}{' '}
            ago
          </p>
          <p className={styles.lastUpdated}>
            Updated: {formatDistanceStrict(new Date(), parseISO(updatedAt))} ago
          </p>
        </div>
        <div className={styles.cardFooter}>
          <div className={styles.assetType}>
            <p className={styles.typeInfo}>
              {isFigma && (
                <span className={styles.figmaIcon}>
                  <FigmaIcon />
                </span>
              )}{' '}
              {checkFileType}
            </p>
            {hasAssetVersioningExperiment && (
              <VersionSelector
                versions={versions.length ? versions : [fallbackVersion]}
                selected={displayedVersion}
                onSelect={setSelectedVersion}
                container={container}
                onSyncNewVersion={
                  canSyncFigma
                    ? () => assetVersioning?.resyncFigmaAsset(asset)
                    : undefined
                }
                onUploadNewVersion={
                  canManageVersions && !canSyncFigma
                    ? file =>
                        assetVersioning?.onUploadChange(
                          { file: { originFileObj: file } } as any,
                          undefined,
                          id
                        )
                    : undefined
                }
                accept={fileType === 'pdf' ? '.pdf,application/pdf' : 'image/*'}
              />
            )}
          </div>
          {getCardAction()}
        </div>
      </div>
    </a>
  );
};
